/**
 * Data Sources Registry and Central Export
 * 
 * This file provides a central access point for all data sources in the application.
 * Each data source is self-contained with its own fetching logic, transformers, and metadata.
 */

// Export types
export * from './types';

// Export shared utilities
export * from './shared/formatters';
export * from './shared/languageMappings';
export * from './shared/utils';

// Import all data source modules
import { objectsDataSource } from './sources/objects';
import { documentsDataSource } from './sources/documents';
import { templatesDataSource } from './sources/templates';
import { stringsDataSource } from './sources/strings';
import { userGamesDataSource } from './sources/userGames';
import { userCompaniesDataSource } from './sources/userCompanies';
import { dialogueLinesDataSource } from './sources/dialogueLines';
import { objectsSyncDataSource } from './sources/objectsSync';
import { favoritesDataSource } from './sources/favorites';

import type { 
  DataSourceType, 
  DataSourceModule, 
  DataSourceConfig, 
  ColumnDefinition,
  FieldMetadata
} from './types';

// Registry mapping data source types to their modules
export const DATA_SOURCE_REGISTRY: Record<DataSourceType, DataSourceModule> = {
  'objects': objectsDataSource,
  'glossary': objectsDataSource, // Deprecated: Use 'objects' instead
  'library': objectsDataSource, // Deprecated: Use 'objects' instead
  'documents': documentsDataSource,
  'templates': templatesDataSource,
  'glossary-blueprints': templatesDataSource, // Deprecated: Use 'templates' instead
  'strings': stringsDataSource,
  'user-games': userGamesDataSource,
  'user-companies': userCompaniesDataSource,
  'dialogue-lines': dialogueLinesDataSource,
  'objects-sync': objectsSyncDataSource,
  'glossary-sync': objectsSyncDataSource, // Deprecated: Use 'objects-sync' instead
  'favorites': favoritesDataSource
};

/**
 * Get a data source module by type
 */
export function getDataSource(type: DataSourceType): DataSourceModule {
  const source = DATA_SOURCE_REGISTRY[type];
  if (!source) {
    throw new Error(`Data source "${type}" not found in registry`);
  }
  return source;
}

/**
 * Get data source configuration by type
 */
export function getDataSourceConfig(type: DataSourceType): DataSourceConfig {
  return getDataSource(type).config;
}

/**
 * Get columns with metadata for a data source
 */
export function getColumnsWithMetadata(type: DataSourceType, defaultLanguage?: string): ColumnDefinition[] {
  const source = getDataSource(type);
  
  // Special handling for strings which has dynamic columns
  if (type === 'strings' && defaultLanguage) {
    // The strings source has a function to get columns, but we'll use the base columns
    return source.columns;
  }
  
  return source.columns;
}

/**
 * Get field metadata for a specific field in a data source
 */
export function getFieldMetadata(type: DataSourceType, fieldKey: string): FieldMetadata | undefined {
  const source = getDataSource(type);
  return source.fields[fieldKey];
}

/**
 * Get all fields metadata for a data source
 */
export function getAllFieldsMetadata(type: DataSourceType): Record<string, FieldMetadata> {
  return getDataSource(type).fields;
}

/**
 * Check if a data source is expandable
 */
export function isDataSourceExpandable(type: DataSourceType): boolean {
  return getDataSource(type).config.expandable;
}

/**
 * Get expandable configuration for a data source
 */
export function getExpandableConfig(type: DataSourceType) {
  const source = getDataSource(type);
  return source.expandableConfig || null;
}

/**
 * Get all available data source types
 */
export function getAvailableDataSources(): DataSourceType[] {
  return Object.keys(DATA_SOURCE_REGISTRY) as DataSourceType[];
}

/**
 * Get all data source configurations
 */
export function getAllDataSourceConfigs(): Record<DataSourceType, DataSourceConfig> {
  const configs: Partial<Record<DataSourceType, DataSourceConfig>> = {};
  
  for (const [type, source] of Object.entries(DATA_SOURCE_REGISTRY)) {
    configs[type as DataSourceType] = source.config;
  }
  
  return configs as Record<DataSourceType, DataSourceConfig>;
}

// Export individual data sources for direct access if needed
export {
  objectsDataSource,
  documentsDataSource,
  templatesDataSource,
  stringsDataSource,
  userGamesDataSource,
  userCompaniesDataSource,
  dialogueLinesDataSource,
  objectsSyncDataSource,
  favoritesDataSource
};

// Export migration helper
export * from './migrationHelper';

// Legacy exports for backward compatibility (can be removed after migration)

// Old TableColumn interface without metadata
export interface TableColumn {
  key: string;
  label: string;
  align?: "left" | "center" | "right";
  className?: string;
  filterable?: boolean;
  editable?: "text" | "dropdown";
}

// Legacy DATA_SOURCES export (old config format)
export const DATA_SOURCES = getAllDataSourceConfigs();

// Legacy DATA_SOURCE_COLUMNS export (without metadata)
const mapColumnsToLegacy = (columns: ColumnDefinition[]): TableColumn[] => 
  columns.map(col => ({
    key: col.key,
    label: col.label,
    align: col.align,
    className: col.className,
    filterable: col.filterable,
    editable: col.editable
  }));

export const DATA_SOURCE_COLUMNS: Record<DataSourceType, TableColumn[]> = {
  'objects': mapColumnsToLegacy(objectsDataSource.columns),
  'glossary': mapColumnsToLegacy(objectsDataSource.columns), // Deprecated
  'library': mapColumnsToLegacy(objectsDataSource.columns), // Deprecated
  'documents': mapColumnsToLegacy(documentsDataSource.columns),
  'templates': mapColumnsToLegacy(templatesDataSource.columns),
  'glossary-blueprints': mapColumnsToLegacy(templatesDataSource.columns), // Deprecated
  'strings': mapColumnsToLegacy(stringsDataSource.columns),
  'user-games': mapColumnsToLegacy(userGamesDataSource.columns),
  'user-companies': mapColumnsToLegacy(userCompaniesDataSource.columns),
  'dialogue-lines': mapColumnsToLegacy(dialogueLinesDataSource.columns),
  'objects-sync': mapColumnsToLegacy(objectsSyncDataSource.columns),
  'glossary-sync': mapColumnsToLegacy(objectsSyncDataSource.columns), // Deprecated
  'favorites': mapColumnsToLegacy(favoritesDataSource.columns)
};

// Legacy getColumnsForDataSource (returns columns without metadata)
export function getColumnsForDataSource(dataSource: DataSourceType, defaultLanguage?: string): TableColumn[] {
  return DATA_SOURCE_COLUMNS[dataSource] || [];
}

// Legacy cardDataTransformers
export const cardDataTransformers = {
  objects: objectsDataSource.transformers.toCard,
  documents: documentsDataSource.transformers.toCard
};

// Legacy EXPANDABLE_DATA_TYPES
export const EXPANDABLE_DATA_TYPES: Record<DataSourceType, any> = {
  'objects': null,
  'glossary': null, // Deprecated
  'library': null, // Deprecated
  'user-games': null,
  'user-companies': null,
  'dialogue-lines': null,
  'templates': null,
  'glossary-blueprints': null, // Deprecated
  'documents': null,
  'objects-sync': null,
  'glossary-sync': null, // Deprecated
  'favorites': null,
  'strings': stringsDataSource.expandableConfig
};

